import { useState, useEffect, useCallback } from 'react';
import { BUILD_HASH, FORCE_UPDATE_FROM_HASHES } from '@/config/version';
import { clearAllCachesAndRefresh, checkForServiceWorkerUpdate, isServiceWorkerStale } from '@/utils/cacheUtils';

const STORED_BUILD_HASH_KEY = 'wormble-build-hash';
const DISMISSED_UPDATE_SESSION_KEY = 'wormble-update-dismissed-session';

export const useVersionCheck = () => {
  const [updateRequired, setUpdateRequired] = useState(false);
  const [isForced, setIsForced] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false);

  // Check for SW updates periodically
  const checkForUpdates = useCallback(async () => {
    // Don't trigger if already dismissed this session
    const dismissedThisSession = sessionStorage.getItem(DISMISSED_UPDATE_SESSION_KEY);
    if (dismissedThisSession) return;
    
    const hasUpdate = await checkForServiceWorkerUpdate();
    if (hasUpdate) {
      setSwUpdateAvailable(true);
      setUpdateRequired(true);
    }
  }, []);

  useEffect(() => {
    const storedHash = localStorage.getItem(STORED_BUILD_HASH_KEY);
    const dismissedThisSession = sessionStorage.getItem(DISMISSED_UPDATE_SESSION_KEY);

    // Only show update modal if stored hash differs from current build
    // After a refresh, these should match, so no modal shown
    if (storedHash && storedHash !== BUILD_HASH) {
      // New build detected - clear any previous session dismissal
      // so user gets notified of this genuinely new update
      sessionStorage.removeItem(DISMISSED_UPDATE_SESSION_KEY);
      
      // Check if this is a forced update
      const forced = FORCE_UPDATE_FROM_HASHES.includes(storedHash);
      setIsForced(forced);
      
      // Show update modal
      setUpdateRequired(true);
    }

    // Update stored hash immediately to prevent re-trigger after refresh
    localStorage.setItem(STORED_BUILD_HASH_KEY, BUILD_HASH);

    // Skip SW staleness checks if we just refreshed (hashes match or just updated)
    // Only check SW staleness if there was a hash mismatch detected
    if (storedHash && storedHash !== BUILD_HASH) {
      isServiceWorkerStale().then(stale => {
        if (stale && !dismissedThisSession) {
          setSwUpdateAvailable(true);
          setUpdateRequired(true);
        }
      });
    }

    // Check for updates when page gains focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdates();
      }
    };

    // Also listen for SW update events
    const handleSwUpdate = () => {
      setSwUpdateAvailable(true);
      setUpdateRequired(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('wormble-update-available', handleSwUpdate);

    // Initial check
    checkForUpdates();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('wormble-update-available', handleSwUpdate);
    };
  }, [checkForUpdates]);

  const dismissUpdate = () => {
    if (!isForced) {
      setIsDismissed(true);
      setUpdateRequired(false);
      sessionStorage.setItem(DISMISSED_UPDATE_SESSION_KEY, 'true');
    }
  };

  const performUpdate = () => {
    window.location.reload();
  };

  // Nuclear option - clear all caches and force refresh
  const forceUpdate = async () => {
    await clearAllCachesAndRefresh();
  };

  return {
    updateRequired: updateRequired && !isDismissed,
    isForced,
    swUpdateAvailable,
    dismissUpdate,
    performUpdate,
    forceUpdate,
    checkForUpdates,
  };
};
